import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { useLive } from '../../lib/hooks.js';
import { useRealtime } from '../../lib/RealtimeContext.jsx';
import { Loading, Empty, StatusPill, Avatar, useToast, PageHeader, Segmented } from '../../components/ui.jsx';
import Icon from '../../components/Icon.jsx';
import { currency, formatDayLabel, timeRange, STATUS_LABEL, todayISO } from '../../lib/format.js';

const TABS = [
  ['upcoming', 'Upcoming'],
  ['past', 'Past'],
  ['all', 'All'],
];

export default function Bookings() {
  const toast = useToast();
  const { unread } = useRealtime();
  const [tab, setTab] = useState('upcoming');

  const fetcher = useCallback(() => api.get('/api/bookings'), []);

  const { data: bookings, loading } = useLive(fetcher, {
    interval: 20000,
    onChange: (next, prev) => {
      const before = new Map(prev.map((b) => [b.id, b.status]));
      for (const b of next) {
        const was = before.get(b.id);
        if (was && was !== b.status && b.status === 'accepted') {
          toast.success(`${b.nurse_name} confirmed your ${b.service_name} booking.`);
        } else if (was && was !== b.status && b.status === 'declined') {
          toast.push(`${b.nurse_name} declined your ${formatDayLabel(b.date)} request.`, 'error');
        }
      }
    },
  });

  if (loading && !bookings) return <Loading rows={3} />;
  if (!bookings)
    return (
      <Empty art="offline" title="Could not load your bookings">
        Check your connection and try again.
      </Empty>
    );

  const today = todayISO();
  const filtered = bookings.filter((b) => {
    if (tab === 'all') return true;
    const active = ['pending', 'accepted'].includes(b.status);
    return tab === 'upcoming' ? active && b.date >= today : !active || b.date < today;
  });

  const needsReview = bookings.filter((b) => b.status === 'completed' && !b.review_id);

  return (
    <>
      <PageHeader title="My bookings" subtitle="Track requests, confirmations and past visits.">
        <Link to="/search" className="btn btn-primary">
          <Icon name="plus" /> Book a nurse
        </Link>
      </PageHeader>

      {needsReview.length > 0 && (
        <div className="banner banner-accent">
          <Icon name="star" />
          <span className="grow">
            You have {needsReview.length} completed visit{needsReview.length > 1 ? 's' : ''} waiting for a rating.
          </span>
          <Link to={`/bookings/${needsReview[0].id}`} className="btn btn-sm">
            Rate now
          </Link>
        </div>
      )}

      <Segmented options={TABS} value={tab} onChange={setTab} className="mb-2" style={{ maxWidth: 400 }} />

      {filtered.length === 0 ? (
        <Empty
          art={tab === 'upcoming' ? 'empty-bookings' : 'inbox'}
          title={tab === 'upcoming' ? 'No upcoming visits' : 'Nothing here yet'}
          action={
            <Link to="/search" className="btn btn-primary">
              <Icon name="search" /> Find a nurse
            </Link>
          }
        >
          {tab === 'upcoming'
            ? 'Book a verified nurse and your confirmed visits will show up here.'
            : 'Completed and cancelled bookings will be listed here.'}
        </Empty>
      ) : (
        <div className="card">
          {filtered.map((b, i) => (
            <Link key={b.id} to={`/bookings/${b.id}`} className="list-row anim-fade" style={{ '--i': i }}>
              <Avatar name={b.nurse_name} avatar={b.nurse_avatar} />
              <div className="grow">
                <div className="list-title truncate">
                  {b.service_name} · {b.nurse_name}
                </div>
                <div className="list-sub truncate">
                  {formatDayLabel(b.date)} · {timeRange(b.start_time, b.end_time)} · {currency(b.amount)}
                  {b.payment_status === 'paid' && ' · paid'}
                </div>
                {b.status === 'completed' && !b.review_id && (
                  <span className="tiny" style={{ color: 'var(--accent)', fontWeight: 600 }}>
                    Rate this visit →
                  </span>
                )}
              </div>
              <div className="row-tight" style={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {unread.byBooking[b.id] > 0 && (
                  <span className="unread-pill" title={`${unread.byBooking[b.id]} unread messages`}>
                    {unread.byBooking[b.id]}
                  </span>
                )}
                <StatusPill status={b.status} label={STATUS_LABEL[b.status]} />
              </div>
              <span className="list-chevron">
                <Icon name="chevron-right" />
              </span>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}
